import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { DishModel } from 'src/app/models/dish.model';
import { FoodItemInflowModel } from 'src/app/models/food-item-inflow.model';
import { type FoodItemOutflowModel } from 'src/app/models/food-item-outflow.model';
import { type FoodItemModel } from 'src/app/models/food-item.model';
import { KardexMovementModel } from 'src/app/models/kardex-movement.model';
import { MenuModel } from 'src/app/models/menu.model';
import { GraphService } from './graph.service';
import { GRAPHQL_INPUTS } from './inputs';

export interface PaginatedFoodItemsSkuModel {
  insumos: FoodItemModel[];
  lastEvaluatedKeyId?: string | null;
}

export interface PaginatedWarehouseFoodInflowsModel {
  inflows: FoodItemInflowModel[];
  nextEvaluatedKeyId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AyBService {
  constructor(private readonly graphService: GraphService) {}

  mutationCreatePlato({
    newDish,
  }: {
    newDish: {
      nombrePlato: string;
      categoria: string;
      observaciones: string;
    };
  }) {
    return this.graphService.mutationAybApi<{
      createPlato: DishModel;
    }>({
      mutation: `mutation CreatePlato($newDish: ${GRAPHQL_INPUTS.InputCreatePlato}!) {
        createPlato(newDish: $newDish) {
          id,
          nombrePlato,
          categoria,
          observaciones,
          insumos {
            id,
            nombreInsumo
          }
        }
      }`,
      variables: { newDish },
    });
  }
  /**
   * Executes GraphQL mutation to create a new warehouse food inflow.
   *
   * @param almacenInsumo - Food inflow DTO object.
   * @returns Observable with mutation response.
   */
  mutationCreateFoodInflow(almacenInsumo: any) {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation CreateFoodInflow($almacenInsumo: inputAlmacen_Dto!){
        createFoodInflow(almacenInsumo: $almacenInsumo){
          id,
          proveedor{
            razonSocial,
            ruc,
            idProveedor
          },
          locacion,
          numFactura,
          numRecibo,
          fileNumber,
          idItinerary,
          observation,
          detalleInsumos{
            nombreInsumo,
            unidadMedida,
            cantidad,
            costo,
            categoria,
            createdAt,
            updatedAt,
            deletedAt
          }
        }
      }`,
      variables: { almacenInsumo },
    });
  }
  mutationCreateMenu(menu: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation CreateMenu($menu: inputMenu_Dto!){
        createMenu(menu: $menu){
          id,
          nombreMenu,
          descripcion
        }
      }`,
      variables: { menu },
    });
  }
  queryGetAllPlatos(): Observable<{
    getAllPlatos: DishModel[];
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllPlatos: DishModel[];
    }>({
      query: `query{
        getAllPlatos{
          id,
          codigoPlato,
          nombrePlato,
          categoria,
          observaciones,
          insumos{
            id,
            cantidad,
            nombreInsumo,
            unidadMedida
          }
        }
      }`,
      variables: {},
    });
  }
  queryGetAllEgresosInsumosByFileId({ fileId }: { fileId: string }) {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllEgresosInsumosByFileId: FoodItemOutflowModel[];
    }>({
      query: `query GetAllEgresosInsumosByFileId($fileId:String!){
        getAllEgresosInsumosByFileId(fileId:$fileId){
          id,
          fileId,
          insumoId,
          serviceName,
          menuName,
          nombre,
          cantidad,
          unidadMedida,
          costo,
          productId,
          fechaServicio,
          IdService,
          day,
          totalesPersonalCampoAYB,
          totalChoferAYB,
          totalGuiaAYB,
          totalMenuXFilaAYB,
          totalPAXAYB,
          totalTCAYB,
          observation,
          hasDocument,
          createdAt,
          updatedAt
        }
      }`,
      variables: { fileId },
    });
  }

  queryGetAllEgresosByAdditionalOutflowId({
    additionalOutflowId,
  }: {
    additionalOutflowId: string;
  }) {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllEgresosByAdditionalOutflowId: FoodItemOutflowModel[];
    }>({
      query: `query GetAllEgresosByAdditionalOutflowId($additionalOutflowId:String!){
        getAllEgresosByAdditionalOutflowId(additionalOutflowId:$additionalOutflowId){
          id,
          fileId,
          insumoId,
          serviceName,
          menuName,
          nombre,
          cantidad,
          unidadMedida,
          costo,
          productId,
          fechaServicio,
          IdService,
          day,
          totalesPersonalCampoAYB,
          totalChoferAYB,
          totalGuiaAYB,
          totalMenuXFilaAYB,
          totalPAXAYB,
          totalTCAYB,
          additionalOutflowId,
          observation,
          hasDocument
        }
      }`,
      variables: { additionalOutflowId },
    });
  }

  queryGetInsumoCosto({ insumoId }: { insumoId: string }) {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getInsumoCosto: number;
    }>({
      query: `query GetInsumoCosto($id:String!){
        getInsumoCosto(id:$id)
      }`,
      variables: { id: insumoId },
    });
  }

  queryGetDishById({ dishId }: { dishId: string }) {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getDishById: DishModel;
    }>({
      query: `query GetDishById($dishId:String!) {
        getDishById(dishId:$dishId) {
          id,
          nombrePlato,
          categoria,
          observaciones,
          insumos {
            id,
            cantidad,
            nombreInsumo,
            unidadMedida
          }
        }
      }`,
      variables: { dishId },
    });
  }

  queryGetInsumoById(id: string) {
    return this.graphService.queryGraphQLAlimentosBebidas<any>({
      query: `query getInsumo($id:String!){
        getInsumo(id:$id){
          id,
          cantidad,
          nombreInsumo,
          unidadMedida,
          proporcion
        }
      }`,
      variables: { id },
    });
  }

  queryGetAllWarehouseFoodInflows(
    nextEvaluatedKeyId?: string | null,
  ): Observable<{
    getAllWarehouseFoodInflows: PaginatedWarehouseFoodInflowsModel;
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllWarehouseFoodInflows: PaginatedWarehouseFoodInflowsModel;
    }>({
      query: `query GetAllWarehouseFoodInflows($nextEvaluatedKeyId: String) {
        getAllWarehouseFoodInflows(nextEvaluatedKeyId: $nextEvaluatedKeyId) {
          inflows {
            id,
            createdAt,
            observation,
            hasAccountingDocuments,
            locacion,
            numFactura,
            fechaFactura,
            acquisitionDate,
            numRecibo,
            fechaRecibo,
            fileNumber,
            proveedor {
              razonSocial,
              ruc,
              idProveedor
            },
            detalleInsumos {
              id,
              nombreInsumo,
              unidadMedida,
              cantidad,
              costo,
              categoria,
              createdAt,
              updatedAt,
              deletedAt
            }
          },
          nextEvaluatedKeyId
        }
      }`,
      variables: {
        nextEvaluatedKeyId: nextEvaluatedKeyId || null,
      },
    });
  }

  queryGetAllFoodItemsSku(lastEvaluatedKeyId?: string | null): Observable<{
    getAllFoodItemsSku: PaginatedFoodItemsSkuModel;
  }> {
    if (lastEvaluatedKeyId === undefined) {
      return from(
        (async () => {
          const allSkus: FoodItemModel[] = [];
          let currentKeyId: string | null = null;

          do {
            const response: {
              getAllFoodItemsSku: PaginatedFoodItemsSkuModel;
            } = await firstValueFrom(
              this.queryGetAllFoodItemsSkuPage(currentKeyId),
            );
            const payload: PaginatedFoodItemsSkuModel | undefined =
              response?.getAllFoodItemsSku;
            const chunk: FoodItemModel[] = payload?.insumos || [];

            if (chunk.length > 0) {
              allSkus.push(...chunk);
            }

            currentKeyId = payload?.lastEvaluatedKeyId || null;
          } while (Boolean(currentKeyId));

          return {
            getAllFoodItemsSku: {
              insumos: allSkus,
              lastEvaluatedKeyId: null,
            },
          };
        })(),
      );
    }

    return this.queryGetAllFoodItemsSkuPage(lastEvaluatedKeyId);
  }

  queryGetAllFoodItemsSkuPage(lastEvaluatedKeyId?: string | null): Observable<{
    getAllFoodItemsSku: PaginatedFoodItemsSkuModel;
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllFoodItemsSku: PaginatedFoodItemsSkuModel;
    }>({
      query: `query GetAllFoodItemsSku($lastEvaluatedKeyId: String) {
         getAllFoodItemsSku(lastEvaluatedKeyId: $lastEvaluatedKeyId) {
            insumos {
              id,
              codigoInsumo,
              nombreInsumo,
              stock,
              stockTeorico,
              peso,
              proporcion,
              unidadMedida,
              categoria,
              estado,
              precioReferencial,
              historialCostos {
                idIngreso,
                costo,
              }
            }
            lastEvaluatedKeyId
          }
      }`,
      variables: { lastEvaluatedKeyId },
    });
  }

  queryGetAllKitchenMenus() {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllKitchenMenus: MenuModel[];
    }>({
      query: `query {
        getAllKitchenMenus {
          id,
          nombreMenu,
          descripcion,
          costoMenu
        }
      }`,
    });
  }

  queryGetAllPlatosByMenuId({ menuId }: { menuId: string }) {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getMenuById: MenuModel;
    }>({
      query: `query GetMenuById($menuId:String!) {
        getMenuById(menuId:$menuId) {
          id,
          nombreMenu,
          descripcion,
          platos{
            id,
            nombrePlato,
            categoria,
            observaciones
            insumos{
            id,
            cantidad,
            nombreInsumo,
            unidadMedida
          }
          }
        }
      }`,
      variables: { menuId },
    });
  }

  queryGetIngreso({ id }: { id: string }): Observable<{
    getIngreso: any;
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getIngreso: any;
    }>({
      query: `query GetIngreso($id:String!){
        getIngreso(id:$id){
          observation,
          hasAccountingDocuments,
            proveedor{
              razonSocial,
              ruc,
              idProveedor
            },
            locacion,
            numFactura,
            fechaFactura,
            id,
           detalleInsumos{
            id,
            nombreInsumo,
            unidadMedida,
            cantidad,
            costo,
            categoria,
            createdAt,
            updatedAt,
            deletedAt
          }
            numRecibo,
            fechaRecibo,
            fileNumber,
        }
      }`,
      variables: { id },
    });
  }

  queryGetMenajeria(id: string): Observable<any> {
    return this.graphService.queryGraphQLAlimentosBebidas<any>({
      query: `query GetMenajeria($id:String!){
        getMenajeria(id:$id){
            id,
            nombre,
            marca,
            modelo,
            cantidad,
            factura{
              razonSocial,
              ruc,
              numFactura
            }
        }
      }`,
      variables: { id },
    });
  }

  queryGetAllMenajeria(): Observable<any> {
    return this.graphService.queryGraphQLAlimentosBebidas<any>({
      query: `query{
            getAllMenajeria{
              id,
              nombre,
              modelo,
              marca,
              cantidad,
              factura{
                razonSocial,
                ruc,
                numFactura
              }
            }
          }`,
      variables: {},
    });
  }
  queryGetMenuById({
    menuId,
  }: {
    menuId: string;
  }): Observable<{ getMenuById: MenuModel }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getMenuById: MenuModel;
    }>({
      query: `query GetMenuById($menuId: String!) {
        getMenuById(menuId: $menuId) {
          id,
          nombreMenu,
          descripcion,
          platos {
            id,
            nombrePlato,
            categoria,
            observaciones,
            insumos {
              id,
              nombreInsumo,
              categoria,
              cantidad,
              unidadMedida
            }
          }
        }
      }`,
      variables: { menuId },
    });
  }
  queryGetAllProvider(): Observable<{
    getAllProvider: {
      id?: string | null;
      codigoProveedor?: string | null;
      cuentaContableDolares?: string | null;
      cuentaContableSoles?: string | null;
      direccionSocial?: string | null;
      distrito?: string | null;
      documento?: string | null;
      nombreComercial?: string | null;
      provincia?: string | null;
      razonSocial?: string | null;
      ruc?: string | null;
      telefono?: string | null;
    }[];
  }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getAllProvider: {
        id?: string | null;
        codigoProveedor?: string | null;
        cuentaContableDolares?: string | null;
        cuentaContableSoles?: string | null;
        direccionSocial?: string | null;
        distrito?: string | null;
        documento?: string | null;
        nombreComercial?: string | null;
        provincia?: string | null;
        razonSocial?: string | null;
        ruc?: string | null;
        telefono?: string | null;
      }[];
    }>({
      query: `query{
         getAllProvider{
            id,
            codigoProveedor,
            cuentaContableDolares,
            cuentaContableSoles,
            direccionSocial,
            distrito,
            documento,
            nombreComercial,
            provincia,
            razonSocial,
            ruc,
            telefono
          }
      }`,
      variables: {},
    });
  }

  mutationCreateMenajeria(menajeria: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation createMenajeria($menajeria: inputMenajeria_Dto!){
        createMenajeria(menajeria: $menajeria){
          id,
          nombre,
          marca,
          modelo,
          cantidad
        }
      }`,
      variables: { menajeria },
    });
  }

  mutationUpdateMenajeria(id: String, menajeria: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation updateMenajeria($id:String!, $menajeria: inputMenajeria_Dto!){
        updateMenajeria(id:$id, menajeria: $menajeria){
          nombre,
          marca,
          modelo,
          cantidad
        }
      }`,
      variables: { id, menajeria },
    });
  }

  mutationCreateNewFoodItemSku(newFoodItemSku: FoodItemModel): Observable<{
    createNewFoodItemSku: {
      id: string;
    };
  }> {
    return this.graphService.mutationAybApi<{
      createNewFoodItemSku: {
        id: string;
      };
    }>({
      mutation: `mutation CreateNewFoodItemSku($newFoodItemSku: inputInsumo_Dto!){
        createNewFoodItemSku(newFoodItemSku: $newFoodItemSku){
          id,
        }
      }`,
      variables: { newFoodItemSku },
    });
  }

  mutationUpdateInsumo({
    insumoId,
    updatedInsumo,
  }: {
    insumoId: string;
    updatedInsumo: Partial<FoodItemModel>;
  }) {
    return this.graphService.mutationAybApi<{
      updateInsumo: any;
    }>({
      mutation: `mutation UpdateInsumo($id:String!, $insumo: inputInsumo_Dto!){
        updateInsumo(id:$id, insumo: $insumo){
          id,
        }
      }`,
      variables: { id: insumoId, insumo: updatedInsumo },
    });
  }

  mutationCreateProvider(provider: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation createProvider($provider:inputProveedor_Dto!){
        createProvider(provider:$provider){
          id,
        }
      }`,
      variables: { provider },
    });
  }

  mutationUpdateProvider(id: string, provider: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation updateProvider($id:String!, $provider: inputProveedor_Dto!){
        updateProvider(id:$id, provider: $provider){
          id,
        }
      }`,
      variables: { id, provider },
    });
  }

  mutationUpdateInsumosStock(insumos: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation updateInsumosStock($insumos: [inputInsumo_Dto!]!){
        updateInsumosStock(insumos: $insumos)
      }`,
      variables: { insumos },
    });
  }

  mutationUpdateSkuRealStocks(
    skuNewStocks: {
      skuId: string;
      newStock: number;
    }[],
  ): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation UpdateMasivoStockInsumos($skuNewStocks: [inputStockUpdate_Dto!]!){
        updateMasivoStockInsumos(skuNewStocks: $skuNewStocks)
      }`,
      variables: { skuNewStocks },
    });
  }

  mutationSubtractInsumoStock(insumo: FoodItemOutflowModel): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation subtractInsumoStock($insumo: egresoInsumo_Dto!) {
        subtractInsumoStock(insumo: $insumo) {
          message,
          success
        }
      }`,
      variables: { insumo },
    });
  }

  mutationAddInsumoStock(foodOutflow: FoodItemOutflowModel): Observable<{
    addInsumoStock: {
      message: string;
      success: boolean;
    };
  }> {
    return this.graphService.mutationAybApi<{
      addInsumoStock: {
        message: string;
        success: boolean;
      };
    }>({
      mutation: `mutation addInsumoStock($insumo: egresoInsumo_Dto!){
        addInsumoStock(insumo: $insumo){
        message,
        success
        }
      }`,
      variables: { insumo: foodOutflow },
    });
  }

  mutationUpdateEgresoInsumoObservation({
    id,
    observation,
  }: {
    id: string;
    observation: string;
  }): Observable<{
    updateEgresoInsumoObservation: FoodItemOutflowModel;
  }> {
    return this.graphService.mutationAybApi<{
      updateEgresoInsumoObservation: FoodItemOutflowModel;
    }>({
      mutation: `mutation UpdateEgresoInsumoObservation($id: String!, $observation: String!) {
        updateEgresoInsumoObservation(id: $id, observation: $observation) {
          id
          observation
          fileId
          insumoId
          serviceName
          menuName
          nombre
          cantidad
          unidadMedida
          costo
          IdService
          productId
          fechaServicio
          day
          totalPAXAYB
          totalTCAYB
          totalGuiaAYB
          totalChoferAYB
          totalMenuXFilaAYB
          totalesPersonalCampoAYB
          additionalOutflowId,
          hasDocument
        }
      }`,
      variables: { id, observation },
    });
  }

  mutationUpdateStockOutflowQuantity({
    egresoInsumoId,
    quantity,
  }: {
    egresoInsumoId: string;
    quantity: number;
  }): Observable<{
    updateEgresoInsumoQuantity: FoodItemOutflowModel;
  }> {
    return this.graphService.mutationAybApi<{
      updateEgresoInsumoQuantity: FoodItemOutflowModel;
    }>({
      mutation: `mutation UpdateEgresoInsumoQuantity($egresoInsumoId:String!,$quantity:Float!) {
        updateEgresoInsumoQuantity(egresoInsumoId:$egresoInsumoId,quantity:$quantity) {
          id
          observation
          fileId
          insumoId
          serviceName
          menuName
          nombre
          cantidad
          unidadMedida
          costo
          IdService
          productId
          fechaServicio
          day
          totalPAXAYB
          totalTCAYB
          totalGuiaAYB
          totalChoferAYB
          totalMenuXFilaAYB
          totalesPersonalCampoAYB
          additionalOutflowId,
          hasDocument
        }
      }`,
      variables: { egresoInsumoId, quantity: quantity },
    });
  }

  mutationCreateFoodItemOutflow(
    newFoodItemOutflow: FoodItemOutflowModel,
  ): Observable<{
    createEgresoInsumo: FoodItemOutflowModel;
  }> {
    return this.graphService.mutationAybApi<{
      createEgresoInsumo: FoodItemOutflowModel;
    }>({
      mutation: `mutation CreateEgresoInsumo($egresoInsumo:egresoInsumo_Dto!){
        createEgresoInsumo(egresoInsumo:$egresoInsumo){
          id
          observation
          fileId
          insumoId
          serviceName
          menuName
          nombre
          cantidad
          unidadMedida
          costo
          IdService
          productId
          fechaServicio
          day
          totalPAXAYB
          totalTCAYB
          totalGuiaAYB
          totalChoferAYB
          totalMenuXFilaAYB
          totalesPersonalCampoAYB
          additionalOutflowId,
          hasDocument
        }
      }`,
      variables: { egresoInsumo: newFoodItemOutflow },
    });
  }

  mutationAddFoodItemToAdditionalOutflow({
    additionalOutflowId,
    foodItemId,
    quantity,
    observation,
  }: {
    additionalOutflowId: string;
    foodItemId: string;
    quantity: number;
    observation: string;
  }): Observable<{ addFoodItemToAdditionalOutflow: FoodItemOutflowModel }> {
    return this.graphService.mutationAybApi<{
      addFoodItemToAdditionalOutflow: FoodItemOutflowModel;
    }>({
      mutation: `mutation AddFoodItemToAdditionalOutflow($additionalOutflowId:String!,$foodItemId:String!,$quantity:Float!,$observation:String!){
        addFoodItemToAdditionalOutflow(additionalOutflowId:$additionalOutflowId,foodItemId:$foodItemId,quantity:$quantity,observation:$observation){
          id,
          fileId,
          insumoId,
          serviceName,
          menuName,
          nombre,
          cantidad,
          unidadMedida,
          costo,
          IdService,
          productId,
          fechaServicio,
          day,
          totalPAXAYB,
          totalTCAYB,
          totalGuiaAYB,
          totalChoferAYB,
          totalMenuXFilaAYB,
          totalesPersonalCampoAYB,
          additionalOutflowId,
          observation
        }
      }`,
      variables: { additionalOutflowId, foodItemId, quantity, observation },
    });
  }
  /**
   * Executes GraphQL mutation to update an existing warehouse food inflow record.
   *
   * @param params - Object containing target inflow id and update payload.
   * @param params.id - Target inflow record ID.
   * @param params.almacenInsumo - Updated inflow payload DTO.
   * @returns Observable with mutation response.
   */
  mutationUpdateIngreso({
    id,
    almacenInsumo,
  }: {
    id: string;
    almacenInsumo: any;
  }) {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation UpdateIngreso($id:String!, $almacenInsumo: inputAlmacen_Dto!){
        updateIngreso(id:$id, almacenInsumo: $almacenInsumo){
            proveedor{
              razonSocial,
              ruc,
              idProveedor
            },
            locacion,
            numFactura,
            numRecibo,
            fileNumber,
            idItinerary,
            id,
           detalleInsumos{
            nombreInsumo,
            unidadMedida,
            cantidad,
            costo,
            categoria,
            createdAt,
            updatedAt,
            deletedAt
          }
        }
      }`,
      variables: { id, almacenInsumo },
    });
  }
  mutationUpdateMenu(id: String, menu: any): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation updateMenu($id:String!, $menu: inputMenu_Dto!){
        updateMenu(id:$id, menu: $menu){
          id,
          nombreMenu,
          descripcion,
          platos{
            id,
            nombrePlato
          }
        }
      }`,
      variables: { id, menu },
    });
  }
  mutationUpdatePlato({
    platoId,
    plato,
  }: {
    platoId: string;
    plato: DishModel;
  }) {
    return this.graphService.mutationAybApi<{
      updatePlato: boolean | null;
    }>({
      mutation: `mutation UpdatePlato($id:String!,$plato:inputPlato_Dto!) {
        updatePlato(id:$id,plato:$plato)
      }`,
      variables: { id: platoId, plato },
    });
  }
  mutationUpdateInsumosFromPlato({ id, plato }: { id: string; plato: any }) {
    return this.graphService.mutationAybApi<{
      updatePlato: any;
    }>({
      mutation: `mutation UpdatePlato($id:String!,$plato:inputPlato_Dto!) {
        updatePlato(id:$id, plato: $plato) {
          id,
          insumos {
            nombreInsumo,
            cantidad,
            unidadMedida,
          }
        }
      }`,
      variables: { id, plato },
    });
  }

  mutationUpdatePlatosFromMenu({
    menuId,
    updatedMenu,
  }: {
    menuId: string;
    updatedMenu: MenuModel;
  }) {
    return this.graphService.mutationAybApi<{
      updateMenu: any;
    }>({
      mutation: `mutation updateMenu($id:String!,$menu:inputMenu_Dto!){
        updateMenu(id:$id,menu:$menu){
          id,
          platos{
            id,
            nombrePlato,
            categoria,
            observaciones,
            costoPlato
          }
        }
      }`,
      variables: { id: menuId, menu: updatedMenu },
    });
  }

  mutationDeleteEgresoInsumo({
    outflowId,
  }: {
    outflowId: string;
  }): Observable<{ mutationDeleteFoodOutflow: boolean }> {
    return this.graphService.mutationAybApi<{
      mutationDeleteFoodOutflow: boolean;
    }>({
      mutation: `mutation DeleteEgresoInsumo($outflowId: String!){
        mutationDeleteFoodOutflow(outflowId: $outflowId) {
          message,
          success
        }
      }`,
      variables: { outflowId },
    });
  }

  mutationDeletePlato(id: string): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation DeletePlato($id: String!){
        deletePlato(id: $id){
          id,
        }
      }`,
      variables: { id },
    });
  }

  mutationDeleteIngreso(id: string): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation deleteIngreso($id: String!){
        deleteIngreso(id: $id){
          id,
        }
      }`,
      variables: { id },
    });
  }

  mutationDeleteMenu(id: string): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation DeleteMenu($id: String!){
        deleteMenu(id: $id){
          id,
        }
      }`,
      variables: { id },
    });
  }

  mutationDeleteSKU(id: string): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation deleteInsumo($id: String!){
        deleteInsumo(id: $id){
          id,
        }
      }`,
      variables: { id },
    });
  }

  mutationDeleteProvider(id: string): Observable<any> {
    return this.graphService.mutationAybApi<any>({
      mutation: `mutation deleteProvider($id: String!){
        deleteProvider(id: $id){
          id,
        }
      }`,
      variables: { id },
    });
  }

  /**
   * Queries the dynamic SSOT merge Kardex ledger for a specific supply code.
   *
   * @param supplyCode - Unique SKU supply code.
   * @param limit - Maximum number of ledger records to retrieve (defaults to 100).
   * @returns Observable<{ getMergeKardexBySupplyCode: KardexMovementModel[] }> - GraphQL query response observable.
   */
  queryGetMergeKardexBySupplyCode(
    supplyCode: string,
    limit: number = 100,
    offset: number = 0,
  ): Observable<{ getMergeKardexBySupplyCode: KardexMovementModel[] }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getMergeKardexBySupplyCode: KardexMovementModel[];
    }>({
      query: `query GetMergeKardexBySupplyCode($supplyCode: String!, $limit: Int, $offset: Int) {
        getMergeKardexBySupplyCode(supplyCode: $supplyCode, limit: $limit, offset: $offset) {
          supplyCode
          id
          movementDate
          movementType
          itineraryId
          itineraryServiceId
          supplySnapshot {
            supplyName
            unitOfMeasure
          }
          inflowSnapshot {
            invoiceNumber
            receiptNumber
            location
            hasAccountingDocuments
          }
          outflowSnapshot {
            outflowType
            location
          }
          itinerarySnapshot {
            fileNumber
          }
          itineraryServiceSnapshot {
            serviceName
            menuName
            itineraryDay
          }
          aybProviderSnapshot {
            providerRuc
            providerName
          }
          observation
          previousQuantityBalance
          previousWeightedAverageUnitPrice
          previousTotalValuationBalance
          movementQuantityDelta
          movementUnitPrice
          movementTotalValuation
          finalQuantityBalance
          finalWeightedAverageUnitPrice
          finalTotalValuationBalance
        }
      }`,
      variables: { supplyCode, limit, offset },
    });
  }

  /**
   * Queries chronological Kardex movements ledger for a specific supply code.
   *
   * @deprecated Delegates to dynamic SSOT merge engine.
   * @param supplyCode - Unique SKU supply code.
   * @param limit - Maximum number of ledger records to retrieve (defaults to 100).
   * @returns Observable<{ getKardexBySupplyCode: KardexMovementModel[] }> - GraphQL query response observable.
   */
  queryGetKardexBySupplyCode(
    supplyCode: string,
    limit: number = 100,
  ): Observable<{ getKardexBySupplyCode: KardexMovementModel[] }> {
    return this.graphService.queryGraphQLAlimentosBebidas<{
      getKardexBySupplyCode: KardexMovementModel[];
    }>({
      query: `query GetKardexBySupplyCode($supplyCode: String!, $limit: Int) {
        getKardexBySupplyCode(supplyCode: $supplyCode, limit: $limit) {
          supplyCode
          movementTimestampId
          id
          supplyId
          movementDate
          movementType
          itineraryId
          itineraryServiceId
          sourceRecordId
          aybProviderId
          serviceId
          supplySnapshot {
            supplyName
            unitOfMeasure
          }
          inflowSnapshot {
            invoiceNumber
            receiptNumber
            inflowType
            location
            hasAccountingDocuments
          }
          outflowSnapshot {
            outflowType
            location
            dispatchDate
          }
          itinerarySnapshot {
            fileNumber
          }
          itineraryServiceSnapshot {
            serviceName
            menuName
            itineraryDay
          }
          aybProviderSnapshot {
            providerRuc
            providerName
          }
          serviceSnapshot {
            serviceName
          }
          observation
          operatorUsername
          previousQuantityBalance
          previousWeightedAverageUnitPrice
          previousTotalValuationBalance
          movementQuantityDelta
          movementUnitPrice
          movementTotalValuation
          finalQuantityBalance
          finalWeightedAverageUnitPrice
          finalTotalValuationBalance
          createdAt
          updatedAt
        }
      }`,
      variables: { supplyCode, limit },
    });
  }
}
